import React, { FC } from 'react';

import {
  DataGroupDatabaseNestedResponse,
  DataGroupResponse,
  JITAccessGroupInfo,
} from '@ariksa/abac/api';
import { NativeResources } from '@ariksa/inventory-core';
import { Box, Stack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  EllipsisText,
  formatTime,
  getIconColor,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { AuthorizedUserIcon, DatabaseIcon } from 'components/Icons';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

import { selectDataGroups } from '../../selectors';

import { PermanentAccessSection } from './PermanentAccessSection';
import { TableSection } from './TableSection';
import { PermanentAccessInfo } from './types';

interface Props {
  dataGroup: DataGroupResponse;
  isLoading?: boolean;
}

const tableStyles = {
  cell: {
    paddingBottom: '11px',
    paddingTop: '11px',
  },
};

export const DataGroupOverviewTables: FC<Props> = props => {
  const { dataGroup, isLoading = false } = props;
  const { authorizedGroups } = useSelector(selectDataGroups);
  const { accountMapping } = useCloudAccountId();
  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();
  const dataSources = dataGroup?.data_group_resources ?? [];
  const groups = authorizedGroups?.data ?? [];

  // TODO: load from the permanent-access endpoint once the backend adds it.
  const permanentAccess: PermanentAccessInfo[] = [];

  const openResource = (resourceType, uuid, accountId) => {
    updateActiveResource({ resourceType, uuid, accountId });
    onOpenMetadata();
  };

  const dataSourceColumns = [
    {
      header: <Box pl="44px">Database</Box>,
      render: ({ row }: { row: DataGroupDatabaseNestedResponse }) => (
        <WithResourceIcon resourceType={NativeResources.SnowflakeDatabases}>
          {row.name}
        </WithResourceIcon>
      ),
      styles: { width: '26%' },
    },
    {
      header: 'Schemas',
      align: 'left',
      render: ({ row }: { row: DataGroupDatabaseNestedResponse }) =>
        (
          <EllipsisText
            label={row.schemas?.map(schema => schema.name).join(', ')}
          />
        ) || '-',
      styles: { width: '36%' },
    },
    {
      header: 'Account',
      align: 'left',
      render: ({ row }: { row: JITAccessGroupInfo }) => {
        const account = accountMapping?.[dataGroup.account_id];
        return account?.name ?? account?.cloud_account_id ?? row.account_id;
      },
      styles: { width: '28%' },
    },
    // {
    //   header: 'Action',
    //   render: ({ row }: { row: DataGroupDatabaseNestedResponse }) => (
    //     <Center onClick={e => e.stopPropagation()}>
    //       <ActionButton
    //         label="View details"
    //         icon={<MonitoringIcon />}
    //         onClick={() =>
    //           openResource(
    //             NativeResources.SnowflakeDatabases,
    //             row.uuid,
    //             dataGroup?.account_id,
    //           )
    //         }
    //       />
    //     </Center>
    //   ),
    //   styles: { width: '10%' },
    // },
  ];

  const authorizedGroupColumns = [
    {
      header: <Box pl="44px">Name</Box>,
      render: ({ row }: { row: JITAccessGroupInfo }) => (
        <WithResourceIcon resourceType={row.identity_type} tooltip={row.name}>
          {row.name}
        </WithResourceIcon>
      ),
      styles: { width: '24%' },
    },
    {
      header: 'Account',
      align: 'left',
      render: ({ row }: { row: JITAccessGroupInfo }) => {
        const account = accountMapping?.[row.account_id];
        return account?.name ?? account?.cloud_account_id ?? row.account_id;
      },
      styles: { width: '23%' },
    },
    {
      header: 'Member Users',
      accessor: 'users',
      styles: {
        width: '15%',
        cell: { textAlign: 'center' },
        //headerCell: tableHeaderStyles,
      },
    },
    {
      header: 'Allowed Roles',
      accessor: 'attached_roles',
      styles: {
        width: '15%',
        cell: { textAlign: 'center' },
        //headerCell: tableHeaderStyles,
      },
    },
    {
      header: 'Last Updated',
      align: 'left',
      render: ({ row }: { row: JITAccessGroupInfo }) =>
        formatTime(row.updated_at, 'DD MMM YYYY'),
      styles: { width: '18%' },
    },
    // {
    //   header: 'Action',
    //   render: ({ row }: { row: JITAccessGroupInfo }) => (
    //     <Center onClick={e => e.stopPropagation()}>
    //       <ActionButton
    //         label="View details"
    //         icon={<MonitoringIcon />}
    //         onClick={() =>
    //           openResource(row.identity_type, row.uuid, row.account_id)
    //         }
    //       />
    //     </Center>
    //   ),
    //   styles: { width: '10%' },
    // },
  ];

  return (
    <Stack spacing={6}>
      <TableSection
        title="Data Sources"
        count={dataSources.length}
        icon={<DatabaseIcon />}
        color={getIconColor(dataGroup?.cloud_provider!)}
      >
        <Table
          columns={dataSourceColumns}
          data={dataSources as any}
          isLoading={isLoading}
          noDataMessage="No data sources"
          onRowClick={(row: DataGroupDatabaseNestedResponse) =>
            row.uuid &&
            openResource(
              NativeResources.SnowflakeDatabases,
              row.uuid,
              dataGroup?.account_id,
            )
          }
          variant=""
          cursor="pointer"
          styles={tableStyles}
        />
      </TableSection>
      <TableSection
        title="Authorized Teams"
        count={groups.length}
        icon={<AuthorizedUserIcon />}
        color={customTheme.colors.blue['300']}
      >
        <Table
          columns={authorizedGroupColumns}
          data={groups}
          isLoading={authorizedGroups.isLoading}
          noDataMessage="No authorized teams"
          onRowClick={(row: JITAccessGroupInfo) =>
            openResource(row.identity_type, row.uuid, row.account_id)
          }
          variant=""
          cursor="pointer"
          styles={tableStyles}
        />
      </TableSection>
      <PermanentAccessSection
        data={permanentAccess}
        tableStyles={tableStyles}
        onOpenResource={openResource}
      />
    </Stack>
  );
};
